/**
 * V-1 — API-klient for hjelpesystemet.
 */
import { authenticatedFetch } from './auth';

export type HelpRoleScope = 'member' | 'member_admin' | 'admin' | 'systemadmin' | 'all';
export type HelpLanguage = 'nb' | 'en';
export type HelpSurface = 'tooltip' | 'panel' | 'page' | 'wizard';

export interface HelpArticle {
  id: string;
  slug: string;
  language: HelpLanguage;
  title: string;
  summary: string;
  body_markdown: string;
  role_scope: HelpRoleScope;
  context_routes: string[];
  category: string;
  tags: string[];
  version: number;
  is_draft: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface HelpArticleListItem {
  id: string;
  slug: string;
  language: HelpLanguage;
  title: string;
  summary: string;
  role_scope: HelpRoleScope;
  category: string;
  tags: string[];
  version: number;
  context_routes: string[];
  updated_at: string;
}

export async function listArticles(opts: {
  category?: string;
  tag?: string;
  q?: string;
  language?: HelpLanguage;
  limit?: number;
} = {}): Promise<{ articles: HelpArticleListItem[]; count: number; language: HelpLanguage }> {
  const params = new URLSearchParams();
  if (opts.category) params.set('category', opts.category);
  if (opts.tag) params.set('tag', opts.tag);
  if (opts.q) params.set('q', opts.q);
  if (opts.language) params.set('language', opts.language);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await authenticatedFetch(`/api/help/articles${qs}`);
  if (!res.ok) throw new Error('Kunne ikke hente artikler');
  return res.json();
}

export async function getArticle(slug: string, language: HelpLanguage = 'nb'): Promise<HelpArticle> {
  const res = await authenticatedFetch(`/api/help/articles/${encodeURIComponent(slug)}?language=${language}`);
  if (res.status === 404) throw new Error('Artikkel ikke funnet');
  if (!res.ok) throw new Error('Kunne ikke hente artikkel');
  const data = await res.json();
  return data.article;
}

export async function getArticlesForRoute(route: string, language: HelpLanguage = 'nb'): Promise<HelpArticleListItem[]> {
  const res = await authenticatedFetch(
    `/api/help/for-route?route=${encodeURIComponent(route)}&language=${language}`
  );
  if (!res.ok) return [];
  const data = await res.json();
  return data.articles || [];
}

export async function logView(slug: string, surface: HelpSurface, route?: string, language: HelpLanguage = 'nb'): Promise<void> {
  try {
    await authenticatedFetch(`/api/help/articles/${encodeURIComponent(slug)}/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ surface, route, language })
    });
  } catch {
    // Fire-and-forget — analytics skal aldri blokkere UX
  }
}

export async function submitSuggestion(route: string, message: string): Promise<void> {
  const res = await authenticatedFetch('/api/help/suggestions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ route, message })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Kunne ikke sende forslag');
  }
}

// ===========================================================================
// Admin API
// ===========================================================================

export async function adminListArticles(opts: {
  include_archived?: boolean;
  include_drafts?: boolean;
  category?: string;
  language?: HelpLanguage;
  q?: string;
  limit?: number;
} = {}): Promise<{ articles: HelpArticle[]; count: number }> {
  const params = new URLSearchParams();
  if (opts.include_archived) params.set('include_archived', '1');
  if (opts.include_drafts === false) params.set('include_drafts', '0');
  if (opts.category) params.set('category', opts.category);
  if (opts.language) params.set('language', opts.language);
  if (opts.q) params.set('q', opts.q);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await authenticatedFetch(`/api/help/admin/articles${qs}`);
  if (!res.ok) throw new Error('Kunne ikke hente artikler');
  return res.json();
}

export async function adminGetArticle(id: string): Promise<HelpArticle> {
  const res = await authenticatedFetch(`/api/help/admin/articles/${id}`);
  if (!res.ok) throw new Error('Kunne ikke hente artikkel');
  const data = await res.json();
  return data.article;
}

export interface AdminArticlePayload {
  slug?: string;
  language?: HelpLanguage;
  title?: string;
  summary?: string;
  body_markdown?: string;
  role_scope?: HelpRoleScope;
  context_routes?: string[];
  category?: string;
  tags?: string[];
  is_draft?: boolean;
}

export async function adminCreateArticle(payload: AdminArticlePayload): Promise<HelpArticle> {
  const res = await authenticatedFetch('/api/help/admin/articles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Kunne ikke opprette');
  }
  const data = await res.json();
  return data.article;
}

export async function adminUpdateArticle(
  id: string,
  payload: AdminArticlePayload & { bump_version?: boolean }
): Promise<{ article: HelpArticle; versioned: boolean }> {
  const res = await authenticatedFetch(`/api/help/admin/articles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Kunne ikke oppdatere');
  }
  return res.json();
}

export async function adminArchiveArticle(id: string): Promise<void> {
  const res = await authenticatedFetch(`/api/help/admin/articles/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Kunne ikke arkivere');
  }
}

export async function adminGetHistory(slug: string): Promise<HelpArticle[]> {
  const res = await authenticatedFetch(`/api/help/admin/articles/by-slug/${encodeURIComponent(slug)}/history`);
  if (!res.ok) throw new Error('Kunne ikke hente historikk');
  const data = await res.json();
  return data.versions;
}

export interface HelpSuggestion {
  id: string;
  route: string;
  user_message: string;
  status: string;
  suggested_by: string | null;
  suggester_name: string | null;
  suggester_email: string | null;
  suggested_role: string | null;
  resolved_article_slug: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export async function adminListSuggestions(status: string = 'pending'): Promise<HelpSuggestion[]> {
  const res = await authenticatedFetch(`/api/help/admin/suggestions?status=${status}`);
  if (!res.ok) throw new Error('Kunne ikke hente forslag');
  const data = await res.json();
  return data.suggestions;
}

export async function adminPatchSuggestion(
  id: string,
  status: 'pending' | 'in_progress' | 'resolved' | 'dismissed',
  resolved_article_slug?: string
): Promise<HelpSuggestion> {
  const res = await authenticatedFetch(`/api/help/admin/suggestions/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, resolved_article_slug })
  });
  if (!res.ok) throw new Error('Kunne ikke oppdatere');
  const data = await res.json();
  return data.suggestion;
}

export interface HelpStats {
  period_days: number;
  counts: { published: number; drafts: number; archived: number; pending_suggestions: number };
  top_articles: { article_slug: string; views: number; unique_roles: number; last_view: string }[];
  top_routes: { route: string; views: number }[];
}

export async function adminGetStats(days: number = 30): Promise<HelpStats> {
  const res = await authenticatedFetch(`/api/help/admin/stats?days=${days}`);
  if (!res.ok) throw new Error('Kunne ikke hente statistikk');
  return res.json();
}

// ===========================================================================
// Visningshjelpere — gjør tekniske slug-er leselige
// ===========================================================================

const CATEGORY_LABELS: Record<string, string> = {
  'study-plan': 'Studieplan',
  'assessment-framework': 'Vurderingsramme',
  'course-instance': 'Kursinstans',
  'exam-set': 'Prøvesett',
  'lms': 'LMS-kobling',
  'veileder': 'Veilederrolle',
  'drift': 'Drift og vedlikehold',
  'esco': 'ESCO og EU-rammeverk',
  'onboarding': 'Introduksjon',
  'wizard': 'Veiviser',
  'general': 'Generelt',
};

/** Returnerer en lesbar norsk etikett for en kategori-slug. */
export function categoryLabel(slug: string | null | undefined): string {
  if (!slug) return 'Generelt';
  if (CATEGORY_LABELS[slug]) return CATEGORY_LABELS[slug];
  // Fallback: gjør slug lesbar (f.eks. "noe-nytt" → "Noe nytt")
  const words = slug.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
